import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { CPA_SECTIONS, cpAssignmentLabel } from "./cpaDsr";

export const EMPTY_DSR_FORM = {
  dials: "",
  connects: "",
  followUpsDue: "",
  followUpsCompleted: "",
  newLeads: "",
  leadLost: "",
  dealLost: "",
  dealLostReason: "",
  connectedCalls: "",
  notes: "",
  dealsMtd: "",
  dealsYtd: "",
  coldLeadsToday: "",
  warmLeadsToday: "",
  hotLeadsToday: "",
  coldLeadsMtd: "",
  warmLeadsMtd: "",
  hotLeadsMtd: "",
  coldToWarm: "",
  warmToHot: "",
  hotToClosed: "",
  opportunityNotes: "",
  proposalsSent: "",
  negotiations: "",
  dealsClosed: "",
  arrClosed: "",
  totalCp: "",
  arrFromCp: "",
  demosCompletedToday: "",
  demosScheduledFuture: "",
  cpProspects: "",
  cpMeetings: "",
  conversationSummary: "",
  additionalNotes: "",
  outboundCalls: "",
  inboundCalls: "",
  callBacks: "",
  liveTransfers: "",
  newLeadsMtd: "",
  newLeadsYtd: "",
  longFollowups: "",
  lgsFollowupCold: "",
  lgsFollowupWarm: "",
  lgsFollowupHot: "",
  transferredClosedLeads: [],
  cpAssignments: [],
  srLgsDealsClosed: "",
  srLgsArrClosed: "",
  inboundDealsClosed: "",
  inboundArrClosed: "",
  whatsApp: "",
  referral: "",
  other: ""
};

export function normalizeDsrCount(value) {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue) || numericValue < 0) return 0;
  return Math.floor(numericValue);
}

export function buildSuggestedLeadTotals(runningTotals = {}, dailyNewLeads = 0, dailyLeadLost = 0) {
  const todayNewLeads = normalizeDsrCount(dailyNewLeads);
  const todayLeadLost = normalizeDsrCount(dailyLeadLost);
  return {
    newLeadsMtd: String(Math.max(0, normalizeDsrCount(runningTotals.baseNewLeadsMtd) + todayNewLeads - todayLeadLost)),
    newLeadsYtd: String(Math.max(0, normalizeDsrCount(runningTotals.baseNewLeadsYtd) + todayNewLeads - todayLeadLost))
  };
}

export function buildSuggestedDealTotals(runningTotals = {}, dailyDealsClosed = 0, dailyDealLost = 0) {
  const todayDealsClosed = normalizeDsrCount(dailyDealsClosed);
  const todayDealLost = normalizeDsrCount(dailyDealLost);
  return {
    dealsMtd: String(Math.max(0, normalizeDsrCount(runningTotals.baseDealsMtd) + todayDealsClosed - todayDealLost)),
    dealsYtd: String(Math.max(0, normalizeDsrCount(runningTotals.baseDealsYtd) + todayDealsClosed - todayDealLost))
  };
}

export function buildSuggestedPipelineTotals(runningTotals = {}, form = {}) {
  return {
    coldLeadsMtd: String(
      normalizeDsrCount(runningTotals.baseColdLeadsMtd) +
      normalizeDsrCount(form.coldLeadsToday)
    ),
    warmLeadsMtd: String(
      normalizeDsrCount(runningTotals.baseWarmLeadsMtd) +
      normalizeDsrCount(form.warmLeadsToday)
    ),
    hotLeadsMtd: String(
      normalizeDsrCount(runningTotals.baseHotLeadsMtd) +
      normalizeDsrCount(form.hotLeadsToday)
    )
  };
}

function NumberField({ label, field, form, onChange, disabled, currency = false, required = false }) {
  return (
    <label className="agent-dsr-field">
      <span>{label}{required ? " *" : ""}</span>
      <div className={currency ? "agent-dsr-input-with-prefix" : ""}>
        {currency && <b aria-hidden="true">$</b>}
        <input
          type="number"
          min="0"
          step={currency ? "0.01" : "1"}
          inputMode={currency ? "decimal" : "numeric"}
          value={form[field]}
          disabled={disabled}
          onChange={(event) => onChange(field, event.target.value)}
          placeholder="0"
          required={required}
        />
      </div>
    </label>
  );
}

function SectionCard({ title, description, wide = false, span = "normal", children }) {
  return (
    <section
      className={`agent-dsr-section agent-dsr-section-${span}${
        wide ? " agent-dsr-section-wide" : ""
      }`}
    >
      <div className="agent-dsr-section-heading">
        <h3>{title}</h3>
        {description && <p>{description}</p>}
      </div>
      <div className="agent-dsr-section-fields">{children}</div>
    </section>
  );
}

function TextField({ label, field, form, onChange, disabled, rows = 4, placeholder, required = false, maxLength }) {
  return (
    <label className="agent-dsr-field agent-dsr-text-field">
      <span>{label}{required ? " *" : ""}</span>
      <textarea
        rows={rows}
        value={form[field] ?? ""}
        required={required}
        maxLength={maxLength}
        disabled={disabled}
        onChange={(event) => onChange(field, event.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

function getRoleConfig(role) {
  const normalized = String(role || "").toLowerCase();
  return {
    isBde: normalized === "bde" || normalized === "sr_bde",
    isLgs: normalized === "lgs",
    isSrLgs: normalized === "sr_lgs",
    isCpa: normalized === "cpa",
    isFs: normalized === "fs",
    isSrBde: normalized === "sr_bde",
    isTl: normalized === "tl"
  };
}

export function validateDsrForm(role, form) {
  const { isBde, isLgs, isFs } = getRoleConfig(role);
  if (isLgs) {
    const value = Number(form.connectedCalls || 0);
    if (!Number.isSafeInteger(value) || value < 0) {
      return "Connected Calls must be a non-negative integer.";
    }
  }
  if ((isBde || isFs) && Number(form.dealLost) > 0 && !String(form.dealLostReason || "").trim()) {
    return "Please provide a reason for the lost deal(s).";
  }
  return "";
}

function displayRoleLabel(role) {
  const { isLgs, isSrLgs, isCpa, isFs, isSrBde, isTl } = getRoleConfig(role);
  if (isTl) return "TL";
  if (isLgs) return "LGS";
  if (isSrLgs) return "SR. LGS";
  if (isCpa) return "CPA";
  if (isFs) return "FS";
  if (isSrBde) return "SR. BDE";
  return "BDE";
}

function formatSummaryValue(value, currency = false) {
  if (currency === "text") return String(value || "Not provided");
  if (currency) {
    return `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }
  if (Array.isArray(value)) return `${value.length} entr${value.length === 1 ? "y" : "ies"}`;
  if (value === undefined || value === null || value === "") return "0";
  return String(value);
}

function getReviewSections(role, form) {
  const { isBde, isLgs, isSrLgs, isCpa } = getRoleConfig(role);

  if (isCpa) return [
    ...CPA_SECTIONS.map(([title, items]) => [title, items.map(([label, field, type]) => [label, field, type === "currency"])]),
    ["CP Assignment", (form.cpAssignments || []).map((entry, index) => [`CP Assign To ${index + 1}`, cpAssignmentLabel(entry)])]
  ];

  if (isLgs || isSrLgs) {
    return [
      ["Lead Activity", [
        ["New leads", "newLeads"],
        ["Lead Lost", "leadLost"],
        ["MTD leads", "newLeadsMtd"],
        ["YTD leads", "newLeadsYtd"],
        ["Call back", "callBacks"],
        ["Live transfer", "liveTransfers"],
        ["Long followup", "longFollowups"]
      ]],
      ["Calls", [["Total calls", "outboundCalls"], ["Inbound calls", "inboundCalls"], ...(isLgs ? [["Connected Calls", "connectedCalls"]] : [])]],
      ["Transferred Leads Closed", (form.transferredClosedLeads || []).map((entry, index) => [
        `Entry ${index + 1}`,
        `${entry.leadCloserName || "No closer selected"} - ${formatSummaryValue(entry.arrClosed, true)}`
      ])],
      ...(isSrLgs ? [
        ["Own Follow-ups", [["Cold", "lgsFollowupCold"], ["Warm", "lgsFollowupWarm"], ["Hot", "lgsFollowupHot"]]],
        ["Own Deal Closed", [["Deals closed", "srLgsDealsClosed"], ["ARR closed", "srLgsArrClosed", true]]]
      ] : []),
      ["Channel Partners", [["CP prospects", "cpProspects"]]],
      ["Lead Sources", [["WhatsApp", "whatsApp"], ["Referral", "referral"], ["Other", "other"]]],
      ...(isLgs ? [["Notes", [["Notes", "notes", "text"]]]] : [])
    ];
  }

  return [
    ["Calling Activity", [["Dials completed", "dials"], ["Connects", "connects"]]],
    ["Follow-up Activity", [["Follow-ups due", "followUpsDue"], ["Follow-ups completed", "followUpsCompleted"]]],
    ["Lead Activity", [
      ["New leads added", "newLeads"],
      ...(!isCpa ? [["MTD leads", "newLeadsMtd"], ["YTD leads", "newLeadsYtd"]] : [])
    ]],
    ...(!isCpa ? [
      ["Pipeline Today", [["Cold", "coldLeadsToday"], ["Warm", "warmLeadsToday"], ["Hot", "hotLeadsToday"]]],
      ["Current Pipeline", [["Cold MTD", "coldLeadsMtd"], ["Warm MTD", "warmLeadsMtd"], ["Hot MTD", "hotLeadsMtd"]]],
      ["Qualified Opportunities", [["Cold to Warm", "coldToWarm"], ["Warm to Hot", "warmToHot"], ["Hot to Closed", "hotToClosed"], ["Opportunity notes", "opportunityNotes"]]]
    ] : []),
    ["Sales Activity", [["Proposals sent", "proposalsSent"], ["Negotiations in progress", "negotiations"]]],
    ["Deals Closed", [
      ["Deals closed today", "dealsClosed"],
      ["ARR closed today", "arrClosed", true]
    ]],
    ...(isBde ? [
      ["Deal Lost", [["Deals Lost", "dealLost"], ["Deal Lost Reason", "dealLostReason", "text"]]]
    ] : []),
    ...(isCpa ? [
      ["Inbound Sales", [["Inbound sales / deals", "inboundDealsClosed"], ["Inbound ARR", "inboundArrClosed", true]]]
    ] : []),
    ["Demos and Meetings", [
      [isCpa ? "CP Meet" : "Demos completed today", "demosCompletedToday"],
      [isCpa ? "CP Meeting Scheduled" : "Demos scheduled for future", "demosScheduledFuture"]
    ]],
    ["Channel Partner Activity", [["Total CP", "totalCp"], ["ARR from CP", "arrFromCp", true], ["CP prospects added", "cpProspects"], ["CP meetings completed", "cpMeetings"]]],
    ["Conversation Summary", [["Summary", "conversationSummary"]]],
    ["Additional Notes", [["Notes", "additionalNotes"]]]
  ];
}

function DsrReviewModal({ role, form, onBack, onCancel, onSubmit, submitting }) {
  const [reviewing, setReviewing] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const sections = getReviewSections(role, form);

  if (confirming) {
    return (
      <div className="agent-dsr-confirm-overlay" role="dialog" aria-modal="true" aria-label="Final DSR confirmation">
        <div className="agent-dsr-confirm-card compact">
          <h3>Are you sure you want to submit this DSR?</h3>
          <p>Once submitted, it will be locked for this business date unless an admin deletes it.</p>
          <div className="agent-dsr-confirm-actions">
            <button type="button" onClick={() => setConfirming(false)} disabled={submitting}>Cancel</button>
            <button type="button" className="primary" onClick={onSubmit} disabled={submitting}>
              {submitting ? "Submitting..." : "Yes, Submit DSR"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!reviewing) {
    return (
      <div className="agent-dsr-confirm-overlay" role="dialog" aria-modal="true" aria-label="Ready to submit DSR">
        <div className="agent-dsr-confirm-card compact">
          <h3>Ready to submit your DSR?</h3>
          <p>You can review every filled detail before sending, or continue straight to final confirmation.</p>
          <div className="agent-dsr-confirm-actions">
            <button type="button" onClick={() => setReviewing(true)} disabled={submitting}>Review DSR</button>
            <button type="button" className="primary" onClick={() => setConfirming(true)} disabled={submitting}>
              Submit DSR
            </button>
            <button type="button" onClick={onCancel} disabled={submitting}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-dsr-confirm-overlay" role="dialog" aria-modal="true" aria-label="Review DSR before submission">
      <div className="agent-dsr-confirm-card">
        <header>
          <span className="agent-dsr-eyebrow">Review before final submit</span>
          <h3>Ready to submit your DSR?</h3>
          <p>Check the details once. Back to Edit keeps everything you entered.</p>
        </header>
        <div className="agent-dsr-review-summary">
          {sections.map(([title, items]) => (
            <section key={title}>
              <h4>{title}</h4>
              {items.length === 0 ? (
                <p className="agent-dsr-review-empty">No entries added.</p>
              ) : (
                <div>
                  {items.map(([label, fieldOrValue, currency]) => (
                    <p key={`${title}-${label}`}>
                      <span>{label}</span>
                      <strong style={currency === "text" ? { whiteSpace: "pre-wrap", overflowWrap: "anywhere" } : undefined}>
                        {Object.prototype.hasOwnProperty.call(form, fieldOrValue)
                          ? formatSummaryValue(form[fieldOrValue], currency)
                          : fieldOrValue}
                      </strong>
                    </p>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
        <div className="agent-dsr-confirm-actions">
          <button type="button" onClick={onBack} disabled={submitting}>Back to Edit</button>
          <button type="button" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button type="button" className="primary" onClick={() => setConfirming(true)} disabled={submitting}>
            Submit DSR
          </button>
        </div>
      </div>
    </div>
  );
}

function CpAssignments({ entries = [], options = [], disabled, onChange }) {
  return <SectionCard title="CP Assignment">
    {entries.map((entry, index) => <div className="cpa-assignment-row" key={index}>
      <label className="agent-dsr-field">
        <span>CP Assign To</span>
        <select aria-label={`CP Assign To ${index + 1}`} value={entry.assignedToId} required disabled={disabled}
          onChange={(event) => {
            const selected = options.find((option) => option.id === event.target.value);
            onChange(entries.map((current, i) => i === index
              ? { assignedToId: selected?.id || '', assignedToName: selected?.name || '' } : current));
          }}>
          <option value="">Select Agent</option>
          {options.map((option) => <option key={option.id} value={option.id}
            disabled={entries.some((other, i) => i !== index && other.assignedToId === option.id)}>
            {option.name} ({option.dsrRoleLabel})
          </option>)}
        </select>
      </label>
      <button type="button" title="Remove assignment" aria-label={`Remove assignment ${index + 1}`}
        disabled={disabled} onClick={() => onChange(entries.filter((_, i) => i !== index))}><Trash2 size={18} /></button>
    </div>)}
    {!options.length && <p role="status">No active sales recipients available.</p>}
    <button type="button" className="cpa-add-assignment" disabled={disabled || !options.length || entries.length >= Math.min(options.length, 20)}
      onClick={() => onChange([...entries, { assignedToId: '', assignedToName: '' }])}>
      <Plus size={16} />{entries.length ? 'Add Another' : 'Add Assignment'}
    </button>
  </SectionCard>;
}

function TransferredClosedLeads({ entries, closers, disabled, onChange }) {
  const safeEntries = Array.isArray(entries) ? entries : [];
  const addEntry = () => {
    onChange([
      ...safeEntries,
      { leadCloserId: "", leadCloserName: "", arrClosed: "" }
    ]);
  };
  const updateEntry = (index, field, value) => {
    const next = safeEntries.map((entry, entryIndex) => {
      if (entryIndex !== index) return entry;
      if (field === "leadCloserId") {
        const closer = closers.find((option) => option.id === value) || {};
        return {
          ...entry,
          leadCloserId: value,
          leadCloserName: closer.name || ""
        };
      }
      return { ...entry, [field]: value };
    });
    onChange(next);
  };
  const removeEntry = (index) => {
    onChange(safeEntries.filter((_, entryIndex) => entryIndex !== index));
  };

  return (
    <SectionCard title="Transferred Leads Closed" description="Leads you transferred that were closed by a BDE" wide>
      <div className="agent-dsr-transfer-list">
        {safeEntries.map((entry, index) => (
          <div className="agent-dsr-transfer-entry" key={index}>
            <label className="agent-dsr-field">
              <span>Lead closer / BDE name</span>
              <select
                value={entry.leadCloserId || ""}
                disabled={disabled}
                onChange={(event) => updateEntry(index, "leadCloserId", event.target.value)}
              >
                <option value="">Select closer</option>
                {closers.map((closer) => (
                  <option value={closer.id} key={closer.id}>
                    {closer.name} ({closer.dsrRoleLabel || closer.dsrRole || "BDE"})
                  </option>
                ))}
              </select>
            </label>
            <label className="agent-dsr-field">
              <span>ARR closed</span>
              <div className="agent-dsr-input-with-prefix">
                <b aria-hidden="true">$</b>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={entry.arrClosed || ""}
                  disabled={disabled}
                  onChange={(event) => updateEntry(index, "arrClosed", event.target.value)}
                  placeholder="0"
                />
              </div>
            </label>
            <button type="button" className="agent-dsr-remove-entry" onClick={() => removeEntry(index)} disabled={disabled}>
              Remove
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="agent-dsr-add-entry" onClick={addEntry} disabled={disabled}>
        + Add Closed Lead
      </button>
    </SectionCard>
  );
}

export default function AgentDsrForm({
  profile,
  form,
  onChange,
  onSubmit,
  submitting,
  message
}) {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [validationMessage, setValidationMessage] = React.useState("");
  const { isBde, isLgs, isSrLgs, isCpa, isTl } = getRoleConfig(profile.dsrRole);
  const roleLabel = displayRoleLabel(profile.dsrRole);
  const alreadySubmitted = Boolean(profile.existingReport);
  const pendingApproval = Boolean(profile.pendingApprovalRequest);
  const disabled = submitting || alreadySubmitted || pendingApproval || profile.loading;
  const runningTotals = profile.runningTotals || {};
  const closerOptions = Array.isArray(profile.leadCloserOptions) ? profile.leadCloserOptions : [];
  React.useEffect(() => {
    if (alreadySubmitted || pendingApproval) setConfirmOpen(false);
  }, [alreadySubmitted, pendingApproval]);
  React.useEffect(() => {
    setValidationMessage("");
  }, [form.connectedCalls, form.dealLost, form.dealLostReason]);
  const validate = () => {
    const error = validateDsrForm(profile.dsrRole, form);
    setValidationMessage(error);
    return !error;
  };
  const handleInitialSubmit = (event) => {
    event.preventDefault();
    if (disabled) return;
    if (!validate()) return;
    setConfirmOpen(true);
  };
  const submitFinal = () => {
    if (disabled) return;
    if (!validate()) {
      setConfirmOpen(false);
      return;
    }
    onSubmit();
  };

  if (isTl) {
    return (
      <section className="agent-dsr-shell agent-dsr-locked sr-bde">
        <header className="agent-dsr-header">
          <div>
            <span className="agent-dsr-eyebrow">Team review role</span>
            <h2>TL Review Workspace</h2>
          </div>
          <div className="agent-dsr-identity" aria-label="DSR identity">
            <div><span>Agent</span><strong>{profile.agentName || "-"}</strong></div>
            <div><span>Role</span><strong>{roleLabel}</strong></div>
            <div><span>PST business date</span><strong>{profile.businessDate || "-"}</strong></div>
          </div>
        </header>
      </section>
    );
  }

  return (
    <form className="agent-dsr-shell" onSubmit={handleInitialSubmit} onInvalid={validate}>
      <header className="agent-dsr-header">
        <div>
          <span className="agent-dsr-eyebrow">Daily sales report</span>
          <h2>{roleLabel} Daily DSR</h2>
          <p>Record today&apos;s activity before closing your shift.</p>
        </div>
        <div className="agent-dsr-identity" aria-label="DSR identity">
          <div><span>Agent</span><strong>{profile.agentName || "-"}</strong></div>
          <div><span>Role</span><strong>{roleLabel}</strong></div>
          <div><span>PST business date</span><strong>{profile.businessDate || "-"}</strong></div>
        </div>
      </header>

      {profile.error && <div className="agent-dsr-message error">{profile.error}</div>}

      {alreadySubmitted ? (
        <div className="agent-dsr-locked">
          <strong>Today&apos;s DSR is submitted and locked.</strong>
          <span>
            Submitted {profile.existingReport?.submittedAt
              ? new Date(profile.existingReport.submittedAt).toLocaleString()
              : "today"}.
          </span>
        </div>
      ) : (
        <>
          {pendingApproval && (
            <div className="agent-dsr-locked pending">
              <strong>Today&apos;s DSR is waiting for TL approval.</strong>
              <span>
                Sent {profile.pendingApprovalRequest?.requestedAt
                  ? new Date(profile.pendingApprovalRequest.requestedAt).toLocaleString()
                  : "today"}.
              </span>
            </div>
          )}

          {isCpa && <div className="agent-dsr-grid agent-dsr-grid-cpa">
            {CPA_SECTIONS.map(([title, items]) => <SectionCard key={title} title={title}>
              {items.map(([label, field, type]) => <NumberField key={field} label={label} field={field}
                form={form} onChange={onChange} disabled={disabled} currency={type === 'currency'} required={field === 'dials'} />)}
            </SectionCard>)}
            <SectionCard title="Notes" wide>
              <TextField label="Notes" field="notes" form={form} onChange={onChange} disabled={disabled} rows={4}
                maxLength={2000} placeholder="Daily updates, partner follow-ups, discussions or other remarks..." />
            </SectionCard>
            <CpAssignments entries={form.cpAssignments} options={profile.cpAssigneeOptions}
              disabled={disabled} onChange={(entries) => onChange('cpAssignments', entries)} />
          </div>}

          {!isCpa && !isLgs && !isSrLgs && (
            <div className="agent-dsr-grid">
              <SectionCard title="Calling Activity" span="double">
                <NumberField
                  label="Dials completed"
                  field="dials"
                  form={form}
                  onChange={onChange}
                  disabled={disabled}
                  required
                />
                <NumberField label="Connects" field="connects" form={form} onChange={onChange} disabled={disabled} />
              </SectionCard>

              <SectionCard title="Follow-up Activity">
                <NumberField label="Follow-ups due" field="followUpsDue" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Follow-ups completed" field="followUpsCompleted" form={form} onChange={onChange} disabled={disabled} />
              </SectionCard>

              <SectionCard title="Lead Activity" span={isCpa ? "normal" : "double"}>
                <NumberField label="New leads added" field="newLeads" form={form} onChange={onChange} disabled={disabled} />
                {!isCpa && (
                  <>
                    <NumberField label="MTD leads" field="newLeadsMtd" form={form} onChange={onChange} disabled={disabled} />
                    <NumberField label="YTD leads" field="newLeadsYtd" form={form} onChange={onChange} disabled={disabled} />
                    <div className="agent-dsr-running-note">
                      Previous totals: MTD {runningTotals.baseNewLeadsMtd || 0}, YTD {runningTotals.baseNewLeadsYtd || 0}. These update when you enter today&apos;s new leads, and you can still edit them manually.
                    </div>
                  </>
                )}
              </SectionCard>

              <SectionCard title="Pipeline Today" description="Current movement for this business date">
                <NumberField label="Cold" field="coldLeadsToday" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Warm" field="warmLeadsToday" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Hot" field="hotLeadsToday" form={form} onChange={onChange} disabled={disabled} />
              </SectionCard>

              <SectionCard title="Current Pipeline" description="Month-to-date pipeline">
                <NumberField label="Cold MTD" field="coldLeadsMtd" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Warm MTD" field="warmLeadsMtd" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Hot MTD" field="hotLeadsMtd" form={form} onChange={onChange} disabled={disabled} />
                <div className="agent-dsr-running-note">
                  Previous pipeline: Cold {runningTotals.baseColdLeadsMtd || 0}, Warm {runningTotals.baseWarmLeadsMtd || 0}, Hot {runningTotals.baseHotLeadsMtd || 0}. Today&apos;s pipeline movement is added automatically.
                </div>
              </SectionCard>

              <SectionCard title="Qualified Opportunities" description="Stage movement counts" span="double">
                <NumberField label="Cold to Warm" field="coldToWarm" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Warm to Hot" field="warmToHot" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Hot to Closed" field="hotToClosed" form={form} onChange={onChange} disabled={disabled} />
                <TextField
                  label="Opportunity notes"
                  field="opportunityNotes"
                  form={form}
                  onChange={onChange}
                  disabled={disabled}
                  rows={3}
                  placeholder="Optional opportunity names, stages, or next steps..."
                />
              </SectionCard>

              <SectionCard title="Sales Activity">
                <NumberField label="Proposals sent" field="proposalsSent" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Negotiations in progress" field="negotiations" form={form} onChange={onChange} disabled={disabled} />
              </SectionCard>

              <SectionCard title="Deals Closed">
                <NumberField label="Deals closed today" field="dealsClosed" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="ARR closed today" field="arrClosed" form={form} onChange={onChange} disabled={disabled} currency />
              </SectionCard>

              {isBde && (
                <SectionCard title="Deal Lost" description="ARR is not reduced">
                  <NumberField label="Deals Lost" field="dealLost" form={form} onChange={onChange} disabled={disabled} />
                  <TextField label="Deal Lost Reason" field="dealLostReason" form={form} onChange={onChange}
                    disabled={disabled} required={Number(form.dealLost) > 0} maxLength={2000} />
                </SectionCard>
              )}

              <SectionCard title="Demos and Meetings">
                <NumberField label={isCpa ? "CP Meet" : "Demos completed today"} field="demosCompletedToday" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label={isCpa ? "CP Meeting Scheduled" : "Demos scheduled for future"} field="demosScheduledFuture" form={form} onChange={onChange} disabled={disabled} />
              </SectionCard>

              {isCpa && (
                <SectionCard title="Inbound Sales">
                  <NumberField label="Inbound sales / deals" field="inboundDealsClosed" form={form} onChange={onChange} disabled={disabled} />
                  <NumberField label="Inbound ARR" field="inboundArrClosed" form={form} onChange={onChange} disabled={disabled} currency />
                </SectionCard>
              )}

              <SectionCard title="Channel Partner Activity">
                <NumberField label="Total CP" field="totalCp" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="ARR from CP" field="arrFromCp" form={form} onChange={onChange} disabled={disabled} currency />
                <NumberField label="CP prospects added" field="cpProspects" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="CP meetings completed" field="cpMeetings" form={form} onChange={onChange} disabled={disabled} />
              </SectionCard>

              <SectionCard title="Conversation Summary" description="Key discussions, objections, and next steps" wide>
                <TextField
                  label="Summary"
                  field="conversationSummary"
                  form={form}
                  onChange={onChange}
                  disabled={disabled}
                  rows={6}
                  placeholder="Summarize important customer conversations from today..."
                />
              </SectionCard>

              <SectionCard title="Additional Notes" description="Optional" wide>
                <TextField
                  label="Notes"
                  field="additionalNotes"
                  form={form}
                  onChange={onChange}
                  disabled={disabled}
                  rows={4}
                  placeholder="Add any other context for your manager..."
                />
              </SectionCard>
            </div>
          )}

          {(isLgs || isSrLgs) && (
            <div className="agent-dsr-grid agent-dsr-grid-lgs">
              <SectionCard title="Lead Activity" span="double">
                <NumberField label="New leads" field="newLeads" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Lead Lost" field="leadLost" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="MTD leads" field="newLeadsMtd" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="YTD leads" field="newLeadsYtd" form={form} onChange={onChange} disabled={disabled} />
                <div className="agent-dsr-running-note">
                  Previous net totals: MTD {runningTotals.baseNewLeadsMtd || 0}, YTD {runningTotals.baseNewLeadsYtd || 0}. New leads add and Lead Lost subtracts from MTD/YTD; you can still edit manually.
                </div>
                <NumberField label="Call back" field="callBacks" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Live transfer" field="liveTransfers" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Long followup" field="longFollowups" form={form} onChange={onChange} disabled={disabled} />
              </SectionCard>
              <SectionCard title="Calls">
                <NumberField label="Total calls" field="outboundCalls" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Inbound calls" field="inboundCalls" form={form} onChange={onChange} disabled={disabled} />
                {isLgs && <NumberField label="Connected Calls" field="connectedCalls" form={form} onChange={onChange} disabled={disabled} />}
              </SectionCard>
              <SectionCard title="Channel Partners">
                <NumberField label="CP prospects" field="cpProspects" form={form} onChange={onChange} disabled={disabled} />
              </SectionCard>
              <TransferredClosedLeads
                entries={form.transferredClosedLeads}
                closers={closerOptions}
                disabled={disabled}
                onChange={(entries) => onChange("transferredClosedLeads", entries)}
              />
              {isSrLgs && (
                <>
                  <SectionCard title="Own Follow-ups" description="Sr. LGS follow-ups they manage directly">
                    <NumberField label="Cold" field="lgsFollowupCold" form={form} onChange={onChange} disabled={disabled} />
                    <NumberField label="Warm" field="lgsFollowupWarm" form={form} onChange={onChange} disabled={disabled} />
                    <NumberField label="Hot" field="lgsFollowupHot" form={form} onChange={onChange} disabled={disabled} />
                  </SectionCard>
                  <SectionCard title="Own Deal Closed">
                    <NumberField label="Deals closed" field="srLgsDealsClosed" form={form} onChange={onChange} disabled={disabled} />
                    <NumberField label="ARR closed" field="srLgsArrClosed" form={form} onChange={onChange} disabled={disabled} currency />
                  </SectionCard>
                </>
              )}
              <SectionCard title="Lead Sources">
                <NumberField label="WhatsApp" field="whatsApp" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Referral" field="referral" form={form} onChange={onChange} disabled={disabled} />
                <NumberField label="Other" field="other" form={form} onChange={onChange} disabled={disabled} />
              </SectionCard>
              {isLgs && <SectionCard title="Notes" wide>
                <TextField label="Notes" field="notes" form={form} onChange={onChange} disabled={disabled} maxLength={2000} />
              </SectionCard>}
            </div>
          )}

          <footer className="agent-dsr-footer">
            <button
              type="submit"
              className="agent-dsr-submit"
              disabled={disabled}
            >
              {submitting ? "Submitting..." : "Submit Daily DSR"}
            </button>
            {validationMessage && <div className="agent-dsr-message error" role="alert">{validationMessage}</div>}
            {!validationMessage && message && (
              <div className={`agent-dsr-message ${message.toLowerCase().includes("success") ? "success" : "error"}`}>
                {message}
              </div>
            )}
          </footer>
          {confirmOpen && (
            <DsrReviewModal
              role={profile.dsrRole}
              form={form}
              submitting={submitting}
              onBack={() => setConfirmOpen(false)}
              onCancel={() => setConfirmOpen(false)}
              onSubmit={submitFinal}
            />
          )}
        </>
      )}
    </form>
  );
}
